import { useCallback, useEffect, useState } from 'react';
import { labCycleAPI, pmaImportAPI } from '../services/api';

const POLL_MS = 20000;

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function useLabCycle({ extendExpiry = false } = {}) {
  const [watch, setWatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionKey, setActionKey] = useState('');
  const [checkingServers, setCheckingServers] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await labCycleAPI.getWatch();
      setWatch(res.data);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'Could not load the lab user cycle'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(() => load({ silent: true }), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const act = useCallback(
    async (account, action) => {
      const verb = action === 'delete' ? 'Delete' : action === 'enable' ? 'Enable' : 'Disable';
      const confirmed = window.confirm(
        action === 'delete'
          ? `Remove the local Linux account ${account.unixUsername} on this server? The home directory stays. A central login with the same name is not deleted. The SMS record stays.`
          : action === 'enable'
            ? extendExpiry
              ? `Enable ${account.unixUsername}? This unlocks the login, starts VNC, sets the account expiry 45 days from today, and restarts the password age. The password itself is not changed.`
              : `Enable ${account.unixUsername}? This unlocks the login and starts VNC. Account expiry and an expired password are not reset.`
            : `Disable ${account.unixUsername}? This locks the login and stops VNC. The home stays.`
      );
      if (!confirmed) return;
      setActionKey(`${account.unixUsername}:${action}`);
      setError('');
      setNotice('');
      try {
        const res = await labCycleAPI.act(account.unixUsername, action);
        if (action === 'delete' && res.data?.message) setNotice(res.data.message);
        await load({ silent: true });
      } catch (err) {
        setError(errorMessage(err, `${verb} failed for ${account.unixUsername}`));
      } finally {
        setActionKey('');
      }
    },
    [load, extendExpiry]
  );

  const checkServers = useCallback(async () => {
    setCheckingServers(true);
    setError('');
    try {
      await pmaImportAPI.runLabUserInventory();
      const started = Date.now();
      while (Date.now() - started < 3 * 60 * 1000) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const progress = await pmaImportAPI.getLabUserInventoryProgress();
        const status = progress.data?.status;
        if (status === 'finished' || status === 'failed') {
          if (status === 'failed') {
            setError(progress.data?.error?.message || 'Server check failed');
          }
          break;
        }
      }
      await load({ silent: true });
    } catch (err) {
      setError(errorMessage(err, 'Could not check the lab servers'));
    } finally {
      setCheckingServers(false);
    }
  }, [load]);

  return { watch, loading, error, notice, actionKey, checkingServers, load, act, checkServers };
}
